"use client";

import React, {createContext, type ReactNode, useState} from "react";
import {useTranslations} from "next-intl";
import {AlertTriangle, Trash2} from "lucide-react";

type ConfirmType = "default" | "danger" | "warning";

type ConfirmOptions = {
    title?: string;
    description?: string;
    confirmText?: string;
    cancelText?: string;
    type?: ConfirmType;
    icon?: React.ReactNode;
};

type ConfirmContextType = (options?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmContextType>(() => {
    console.error("ConfirmDialogProvider must be used within a provider");
    return Promise.resolve(false);
});

export const useConfirm = () => {
    const context = React.useContext(ConfirmContext);
    if (!context) {
        console.error("useConfirm must be used within ConfirmDialogProvider");
        return () => Promise.resolve(false);
    }
    return context;
};

// noinspection JSUnusedGlobalSymbols
export const ConfirmDialogProvider = ({children}: {children: ReactNode}) => {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<ConfirmOptions>({});
    const [resolvePromise, setResolvePromise] = useState<(value: boolean) => void>();

    const tCommon = useTranslations("common");

    const confirm = (opts?: ConfirmOptions): Promise<boolean> => {
        setOptions(opts || {});
        setOpen(true);
        return new Promise<boolean>((resolve) => {
            setResolvePromise(() => resolve);
        });
    };

    const handleClose = (result: boolean) => {
        setOpen(false);
        resolvePromise?.(result);
    };

    const getIcon = () => {
        if (options.icon) return options.icon;
        if (options.type === "danger") return <Trash2 size={22} className="text-red-500" />;
        if (options.type === "warning") return <AlertTriangle size={22} className="text-orange-500" />;
        return null;
    };

    const defaultTitle =
        options.type === "danger"
            ? tCommon("delete") || "Delete item"
            : options.type === "warning"
              ? "Warning"
              : "Are you sure?";

    const defaultDescription =
        options.type === "danger"
            ? "Are you sure you want to delete this item? This action cannot be undone."
            : options.type === "warning"
              ? "Please be careful with this action."
              : "Are you sure you want to continue?";

    const confirmBtnClass =
        options.type === "danger"
            ? "px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
            : "px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors";

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}

            {open && (
                <div
                    className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    onClick={() => handleClose(false)}
                >
                    <div
                        className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center gap-3 px-6 pt-6 pb-2">
                            {getIcon()}
                            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                                {options.title || defaultTitle}
                            </h2>
                        </div>

                        <div className="px-6 py-4">
                            <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-line">
                                {options.description || defaultDescription}
                            </p>
                        </div>

                        <div className="flex justify-end gap-3 px-6 pb-6 pt-2">
                            <button
                                onClick={() => handleClose(false)}
                                className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                {options.cancelText || tCommon("cancel")}
                            </button>
                            <button
                                onClick={() => handleClose(true)}
                                className={confirmBtnClass}
                            >
                                {options.confirmText || tCommon("confirm")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </ConfirmContext.Provider>
    );
};

export default ConfirmDialogProvider;
